import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'
import { useShadowDomContext } from '@strata-monorepo/shared-ui/utils/ShadowDom/context'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal: React.FC<React.ComponentProps<typeof DialogPrimitive.Portal>> = ({
  ...props
}) => {
  const { containerPortalContainer } = useShadowDomContext()
  return (
    <DialogPrimitive.Portal
      container={containerPortalContainer || undefined}
      {...props}
    />
  )
}

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'strata:fixed strata:inset-0 strata:z-50 strata:bg-black/80 data-[state=open]:strata:animate-in data-[state=closed]:strata:animate-out data-[state=closed]:strata:fade-out-0 data-[state=open]:strata:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'strata:fixed strata:left-[50%] strata:top-[50%] strata:z-50 strata:grid strata:w-full strata:max-w-lg strata:translate-x-[-50%] strata:translate-y-[-50%] strata:gap-4 strata:border strata:bg-white strata:p-6 strata:shadow-lg strata:duration-200 data-[state=open]:strata:animate-in data-[state=closed]:strata:animate-out data-[state=closed]:strata:fade-out-0 data-[state=open]:strata:fade-in-0 data-[state=closed]:strata:zoom-out-95 data-[state=open]:strata:zoom-in-95 data-[state=closed]:strata:slide-out-to-left-1/2 data-[state=closed]:strata:slide-out-to-top-[48%] data-[state=open]:strata:slide-in-from-left-1/2 data-[state=open]:strata:slide-in-from-top-[48%] strata:rounded-lg sm:strata:rounded-lg',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className='strata:absolute strata:right-4 strata:top-4 strata:rounded-sm strata:opacity-70 strata:ring-offset-white strata:transition-opacity hover:strata:opacity-100 focus:strata:outline-none focus:strata:ring-2 focus:strata:ring-gray-950 focus:strata:ring-offset-2 disabled:strata:pointer-events-none data-[state=open]:strata:bg-gray-100 data-[state=open]:strata:text-gray-500'>
        <X className='strata:h-4 strata:w-4' />
        <span className='strata:sr-only'>Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'strata:flex strata:flex-col strata:space-y-1.5 strata:text-center sm:strata:text-left',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'strata:flex strata:flex-col-reverse sm:strata:flex-row sm:strata:justify-end sm:strata:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'strata:text-lg strata:font-semibold strata:leading-none strata:tracking-tight',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('strata:text-sm strata:text-gray-500', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
